import React from 'react';
import styles from '@/components/EditorCampos/EditorCampos.module.css';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
// Importamos o CampoTexto em vez de CampoSelect, já que parece não existir
import { mdiPlusCircleOutline, mdiDelete, mdiContentSaveOutline, mdiClose, mdiCircle } from '@mdi/js';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import { portTypes } from '@tera/shared/src/fluxoConfig/ports/portType';
import Icon from '@/components/Icon';
import themes from '@tera/shared/src/fluxoConfig/themes';
import Grid, { GridItem } from '@/components/layout/Grid';

// Tipos
type TipoMetadado = 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'timestamp' | 'list' | 'object';

interface Metadado {
    nome: string;
    tipo: TipoMetadado;
    listaTipo?: TipoMetadado;
    metadados?: Metadado[];
    valorPadrao?: any;
}

interface EstruturaDados {
    id?: number;
    nome: string;
    descricao: string;
    metadados: Metadado[];
    organizacao_id?: number;
}

interface FormEstruturaDadosProps {
    value?: EstruturaDados | null;
    onChange?: (value: EstruturaDados) => void;
    fieldErrors?: Record<string, string>;
}

const tiposOptions = portTypes.map(it => ({
    id: it.type,
    label: <span>
        <Icon path={mdiCircle} size={0.8} color={themes.common.ports[it.type as keyof typeof themes.common.ports]?.color as string ?? 'gray'} /> {it.label}
    </span>
}))

type MetadadosProps = {
    value: Metadado[];
    onChange: (metadados: Metadado[]) => void;
    path?: number[];
    fieldErrors?: Record<string, string>;
}

const Metadados = ({ value, path = [], onChange, fieldErrors }: MetadadosProps) => {
    return <div data-testId={`metadados-container-${path.join('-')}`}>
        {value.map((metadado, index) => {
            const currentPath = [...path, index];

            return (
                <div data-testId={`metadado-container-${currentPath.join('-')}`} key={`metadado-container-${currentPath.join('-')}`} className={styles.campoContainer}>
                    <div className={styles.campoHeader}>
                        <HBox gap={8} stretch style={{ width: '100%' }}>
                            <Grid style={{ width: '100%' }}>
                                <GridItem sm={12} md={4}>
                                    <CampoTexto
                                        instanceId={`metadado-nome-${currentPath.join('-')}`}
                                        label="Nome"
                                        value={metadado.nome}
                                        onChange={(valor) => onChange([
                                            ...value.slice(0, index),
                                            { ...metadado, nome: valor },
                                            ...value.slice(index + 1)
                                        ])}
                                        error={fieldErrors?.[`${path.map(p => `metadados[${p}].`)}nome`]}
                                    />

                                </GridItem>
                                <GridItem sm={12} md={metadado.tipo === 'object' ? 8 : 4}>
                                    <CampoSeletor
                                        instanceId={`metadado-tipo-${currentPath.join('-')}`}
                                        options={tiposOptions}
                                        label="Tipo"
                                        value={metadado.tipo}
                                        onChange={(valor) => onChange([
                                            ...value.slice(0, index),
                                            {
                                                ...metadado,
                                                tipo: valor,
                                                metadados: valor === 'object' ? [] : undefined,
                                                listaTipo: valor === 'list' ? 'string' : undefined
                                            } as Metadado,
                                            ...value.slice(index + 1)
                                        ])}
                                        error={fieldErrors?.[`${path.map(p => `metadados[${p}].`)}tipo`]}
                                    />
                                </GridItem>
                                <GridItem sm={12} md={4}>
                                    {metadado.tipo === 'list' && (
                                        <CampoSeletor
                                            instanceId={`metadado-lista-tipo-${currentPath.join('-')}`}
                                            options={tiposOptions}
                                            label="Tipo dos itens da lista"
                                            value={metadado.listaTipo || 'string'}
                                            onChange={(valor) => onChange([
                                                ...value.slice(0, index),
                                                {
                                                    ...metadado,
                                                    listaTipo: valor,
                                                    metadados: undefined // Limpa metadados se for uma lista
                                                } as Metadado,
                                                ...value.slice(index + 1)
                                            ])}
                                            error={fieldErrors?.[`${path.map(p => `metadados[${p}].`)}listaTipo`]}
                                        />

                                    )}
                                    {metadado.tipo !== 'object' && metadado.tipo !== 'list' && (
                                        <CampoTexto
                                            instanceId={`metadado-valor-padrao-${currentPath.join('-')}`}
                                            label="Valor padrão (opcional)"
                                            value={metadado.valorPadrao || ''}
                                            onChange={(valor) => onChange([
                                                ...value.slice(0, index),
                                                { ...metadado, valorPadrao: valor },
                                                ...value.slice(index + 1)
                                            ])}
                                            error={fieldErrors?.[`${path.map(p => `metadados[${p}].`)}valorPadrao`]}
                                        />
                                    )}
                                </GridItem>
                            </Grid>
                            <Botao
                                variant="none-danger"
                                title="Remover"
                                icon={mdiDelete}
                                onClick={() => onChange(value.filter((_, i) => i !== index))}
                            />
                        </HBox>
                    </div>

                    {metadado.tipo === 'object' && (
                        <div className={styles.campoNested}>
                            <VBox gap={8}>
                                <Metadados
                                    value={metadado.metadados ?? []}
                                    path={currentPath}
                                    onChange={(v) => onChange([
                                        ...value.slice(0, index),
                                        { ...metadado, metadados: v },
                                        ...value.slice(index + 1)
                                    ])}
                                    fieldErrors={fieldErrors}
                                />
                            </VBox>
                        </div>
                    )}
                </div>
            );
        })}
        <Botao
            variant="outline-primary"
            title="Adicionar propriedade"
            icon={mdiPlusCircleOutline}
            onClick={() => onChange([...value, { nome: '', tipo: 'string' }])}
        >Adicionar propriedade</Botao>
    </div>
};

const FormEstruturaDados: React.FC<FormEstruturaDadosProps> = ({ value, onChange, fieldErrors }: FormEstruturaDadosProps) => {
    if (!value) {
        return null
    }

    return (
        <Grid>
            <GridItem sm={12} md={6}>
                <CampoTexto
                    instanceId="nome"
                    label="Nome da estrutura"
                    value={value?.nome ?? ''}
                    onChange={(v) => onChange?.({ ...(value ?? {}), nome: v } as EstruturaDados)}
                    style={{ flex: 1 }}
                    error={fieldErrors?.nome}
                />
            </GridItem>
            <GridItem sm={12} md={6}>
                <CampoTexto
                    instanceId="descricao"
                    label="Descrição"
                    value={value?.descricao ?? ''}
                    onChange={(v) => onChange?.({ ...(value ?? {}), descricao: v } as EstruturaDados)}
                    multiline
                    error={fieldErrors?.descricao}
                />
            </GridItem>

            <GridItem sm={12}>
                <h3>Metadados</h3>
                <p>Defina as propriedades desta estrutura de dados</p>

                <VBox gap={8} style={{ marginTop: '16px' }}>
                    <Metadados
                        value={value?.metadados ?? []}
                        onChange={(metadados) => onChange?.({ ...(value ?? {}), metadados } as EstruturaDados)}
                        fieldErrors={fieldErrors}
                    />
                </VBox>
            </GridItem>
        </Grid>
    );
};

export default FormEstruturaDados;
